import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Unplug } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/owner/canva`;
const ERR = (e, d) => e?.response?.data?.detail || d;

const DesignGrid = ({ items, busyId, onPick }) => (
  <div className="max-h-[55vh] overflow-y-auto pl-1" data-testid="canva-grid"><div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
    {items.map((d) => (
      <button key={d.id} type="button" disabled={!!busyId} onClick={() => onPick(d)}
        className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 text-right transition-shadow hover:shadow-lg disabled:opacity-60" data-testid={`canva-design-${d.id}`}>
        <div className="h-32 shrink-0 bg-slate-100 p-1.5">{d.thumb && <img src={d.thumb} alt={d.title} loading="lazy" className="h-full w-full object-contain transition-transform group-hover:scale-105" />}</div>
        <p className="truncate px-2 py-1.5 text-xs font-semibold text-slate-700">{d.title}</p>
        {busyId === d.id && <div className="absolute inset-0 grid place-items-center bg-white/80"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>}
      </button>
    ))}
  </div></div>
);

export const CanvaImport = ({ onImported }) => {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  useEffect(() => { axios.get(`${API}/status`).then((r) => setSt(r.data)).catch(() => setSt({ configured: false })); }, []);
  const load = async (cont = null) => {
    setLoading(true);
    try { const r = await axios.get(`${API}/designs`, { params: { continuation: cont || "", query: q } }); setItems((v) => (cont ? [...v, ...r.data.items] : r.data.items)); setNext(r.data.continuation); }
    catch (e) { if (e?.response?.status === 409) setSt((s) => ({ ...s, connected: false })); toast.error(ERR(e, "تعذّر جلب التصاميم")); }
    finally { setLoading(false); }
  };
  const connect = async () => {
    try { const r = await axios.get(`${API}/connect`); window.location.href = r.data.url; } catch (e) { toast.error(ERR(e, "تعذّر بدء الربط")); }
  };
  const start = () => { if (!st?.connected) return connect(); setOpen(true); load(); };
  const pick = async (d) => {
    setBusyId(d.id);
    try {
      let r = await axios.post(`${API}/import`, { design_id: d.id }, { timeout: 90000 });
      for (let i = 0; i < 10 && r.data.status === "in_progress"; i++) r = await axios.post(`${API}/import`, { design_id: d.id, job_id: r.data.job_id }, { timeout: 90000 });
      if (r.data.status !== "done") throw new Error();
      onImported(r.data.image_id); toast.success(`تم استيراد «${d.title}»`); setOpen(false);
    } catch (e) { toast.error(ERR(e, "تعذّر استيراد التصميم")); } finally { setBusyId(""); }
  };
  const disconnect = async () => { await axios.delete(`${API}/connect`); setSt((s) => ({ ...s, connected: false })); setOpen(false); toast.success("تم فك ربط كانفا"); };
  if (!st?.configured) return null;
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={start} className="gap-1.5 border-[#7d2ae8]/40 text-[#7d2ae8] hover:bg-[#7d2ae8]/5" data-testid="ad-canva-import">
        <span className="grid h-4 w-4 place-items-center rounded-full bg-gradient-to-br from-[#00c4cc] to-[#7d2ae8] text-[9px] font-black text-white">C</span>
        {st.connected ? "استيراد من كانفا" : "ربط كانفا"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" dir="rtl" data-testid="canva-dialog">
          <DialogHeader className="pl-8 text-right">
            <DialogTitle>اختر تصميماً من كانفا</DialogTitle>
            <DialogDescription>يتم تصدير الصفحة الأولى بصيغة PNG وتصير صورة الإعلان. المقاس المثالي 1500×500.</DialogDescription>
          </DialogHeader>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); load(); }}>
            <div className="relative flex-1"><Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في تصاميمك…" className="pr-9" data-testid="canva-search" /></div>
            <Button type="submit" variant="outline" disabled={loading} data-testid="canva-search-btn">بحث</Button>
          </form>
          {loading && !items.length ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
            : items.length ? <DesignGrid items={items} busyId={busyId} onPick={pick} />
            : <p className="py-10 text-center text-sm text-slate-400">لا توجد تصاميم</p>}
          <div className="flex items-center justify-between">
            <button type="button" onClick={disconnect} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-600" data-testid="canva-disconnect"><Unplug className="h-3.5 w-3.5" /> فك ربط كانفا</button>
            {next && <Button variant="ghost" size="sm" disabled={loading} onClick={() => load(next)} data-testid="canva-more">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "تحميل المزيد"}</Button>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
